import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, CreditCard, Download, Mail, Phone, Workflow } from 'lucide-react';
import type {
  AdminLocalProcessorSettingsResponse,
  AdminNumbersResponse,
  AdminStaffProfileSetupCompletenessResponse,
  StaffGoogleWorkspaceAuthorizationStartResponse,
  ServerBillingStatus,
} from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';
import { engineStartServiceLink } from '../lib/oracord-engine-setup-command';
import { API_BASE_URL } from '../lib/config';
import { authClient } from '../lib/auth-client';
import { useSessionStore } from '../stores/session';
import { engineSetupCheckDone, engineSetupStatusItems } from '../lib/engine-setup-checks';
import { describeEngineDownload } from '../lib/engine-download';
import {
  describeEngineHandoffStatus,
  type SetupSourceStatus,
} from '../lib/setup-source-status';
import { PageHeader, SectionCard } from '../components/WebPresentation';
import { AdminActionDialog } from '../components/AdminActionDialog';

export default function Setup() {
  const [billing, setBilling] = useState<ServerBillingStatus | null>(null);
  const [engine, setEngine] = useState<AdminLocalProcessorSettingsResponse | null>(null);
  const [staffSetup, setStaffSetup] = useState<AdminStaffProfileSetupCompletenessResponse | null>(null);
  const [businessLines, setBusinessLines] = useState<AdminNumbersResponse | null>(null);
  const { data: sessionData } = authClient.useSession();
  const activeOrganizationId = useSessionStore(state => state.activeOrganizationId);
  const [openedSetupLink, setOpenedSetupLink] = useState(false);
  const [installerDownloaded, setInstallerDownloaded] = useState(false);
  const [installerAcknowledged, setInstallerAcknowledged] = useState(false);
  const [googleAuthorizationStarting, setGoogleAuthorizationStarting] = useState(false);
  const [googleAuthorizationError, setGoogleAuthorizationError] = useState<string | null>(null);
  const [engineLaunchDialog, setEngineLaunchDialog] = useState<{
    href: string;
    kind: 'pair' | 'start';
  } | null>(null);

  const load = useCallback(async () => {
    const [billingResult, engineResult, staffSetupResult, businessLinesResult] = await Promise.allSettled([
      apiFetch<ServerBillingStatus>('/api/billing/status'),
      apiFetch<AdminLocalProcessorSettingsResponse>('/api/admin/settings/local-processor'),
      apiFetch<AdminStaffProfileSetupCompletenessResponse>('/api/admin/settings/staff-profile-setup-completeness'),
      apiFetch<AdminNumbersResponse>('/api/admin/numbers'),
    ]);
    if (billingResult.status === 'fulfilled') setBilling(billingResult.value);
    if (engineResult.status === 'fulfilled') setEngine(engineResult.value);
    if (staffSetupResult.status === 'fulfilled') setStaffSetup(staffSetupResult.value);
    if (businessLinesResult.status === 'fulfilled') setBusinessLines(businessLinesResult.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const engineReady = describeEngineHandoffStatus({ openedSetupLink, engine }).state === 'ready';
    if (!openedSetupLink || engineReady) return undefined;
    const interval = window.setInterval(() => {
      void load();
    }, 3000);
    return () => window.clearInterval(interval);
  }, [engine, load, openedSetupLink]);

  const storageScope = engine?.organizationId ?? sessionData?.user?.id ?? null;

  useEffect(() => {
    if (!storageScope) {
      setInstallerDownloaded(false);
      setInstallerAcknowledged(false);
      return;
    }
    const downloaded = window.localStorage.getItem(installerStorageKey(storageScope, 'downloaded')) === 'true';
    const acknowledged = window.localStorage.getItem(installerStorageKey(storageScope, 'installed-acknowledged')) === 'true';
    setInstallerDownloaded(downloaded || acknowledged);
    setInstallerAcknowledged(acknowledged);
  }, [storageScope]);

  const recordInstallerDownloaded = useCallback(() => {
    if (storageScope) {
      window.localStorage.setItem(installerStorageKey(storageScope, 'downloaded'), 'true');
    }
    setInstallerDownloaded(true);
  }, [storageScope]);

  const acknowledgeInstaller = useCallback(() => {
    if (storageScope) {
      window.localStorage.setItem(installerStorageKey(storageScope, 'downloaded'), 'true');
      window.localStorage.setItem(installerStorageKey(storageScope, 'installed-acknowledged'), 'true');
    }
    setInstallerDownloaded(true);
    setInstallerAcknowledged(true);
  }, [storageScope]);

  const engineDownload = describeEngineDownload(engine?.engine);
  const pairingLaunchLink = activeOrganizationId ? buildOracordEnginePairingLaunchLink(activeOrganizationId) : '#';
  const engineStartLink = engineStartServiceLink();
  const engineInstalled = engineSetupCheckDone(engine?.engine, 'Engine installed');
  const businessSetupLoaded = engineSetupCheckDone(engine?.engine, 'Business setup loaded');
  const engineRunning = engineSetupCheckDone(engine?.engine, 'Running');
  const billingActive = Boolean(billing?.active);
  const activePlan = billing?.plan ?? null;
  const soloMembership = billingActive && isSoloPlan(activePlan);
  const engineHandoffStatus = describeEngineHandoffStatus({ openedSetupLink, engine });
  const engineHandoffReady = engineHandoffStatus.state === 'ready';
  const effectiveInstallerAcknowledged = engineInstalled || installerAcknowledged;
  const engineCoreSetupComplete = engineInstalled && engineHandoffReady;
  const showEngineInstallCard = !installerDownloaded && !effectiveInstallerAcknowledged;
  const engineStatusItems = engineSetupStatusItems(engine?.engine).map(item =>
    item.mark === 'Engine installed' && effectiveInstallerAcknowledged
      ? { ...item, done: true }
      : item,
  );

  const openPairingSetupLink = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setEngineLaunchDialog({ href: pairingLaunchLink, kind: 'pair' });
  }, [pairingLaunchLink]);

  const openEngineStartLink = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setEngineLaunchDialog({ href: engineStartLink, kind: 'start' });
  }, [engineStartLink]);

  const confirmEngineLaunch = useCallback(() => {
    if (!engineLaunchDialog) return;
    const { href, kind } = engineLaunchDialog;
    setEngineLaunchDialog(null);
    if (kind === 'pair') {
      setOpenedSetupLink(true);
      window.setTimeout(() => void load(), 1500);
    } else {
      window.setTimeout(() => void load(), 3000);
    }
    window.location.href = href;
  }, [engineLaunchDialog, load]);

  const startGoogleAuthorization = useCallback(async () => {
    setGoogleAuthorizationStarting(true);
    setGoogleAuthorizationError(null);
    try {
      const response = await apiFetch<StaffGoogleWorkspaceAuthorizationStartResponse>('/api/auth/google-workspace/authorization/start', {
        method: 'POST',
      });
      if (response.authorizationUrl && response.redirectAvailable) {
        window.location.assign(response.authorizationUrl);
        return;
      }
      setGoogleAuthorizationError('Google authorization is not ready yet. Refresh Setup and try again.');
    } catch (err) {
      setGoogleAuthorizationError(err instanceof ApiError && err.message
        ? err.message
        : 'Could not start Google authorization. Refresh Setup and try again.');
    } finally {
      setGoogleAuthorizationStarting(false);
    }
  }, []);

  const activeProfileCount = staffSetup?.summary.activeProfiles ?? 0;
  const googleWorkspaceNeedsSetup = staffSetup?.summary.needsGoogleWorkspace ?? 0;
  const googleWorkspaceConfigured = staffSetup?.googleWorkspace.configured ?? false;
  const soloGoogleConnected = soloMembership && googleWorkspaceConfigured && activeProfileCount > 0 && googleWorkspaceNeedsSetup === 0;
  const businessLineCount = businessLines?.rows.length ?? 0;
  const businessLineConnected = businessLineCount > 0;
  const signedInEmail = sessionData?.user?.email ?? null;
  const signedInDomain = emailDomain(signedInEmail);
  const signedInWithPersonalGoogle = signedInDomain === 'gmail.com' || signedInDomain === 'googlemail.com';
  const showGoogleAccountMismatchNote = signedInWithPersonalGoogle && Boolean(signedInEmail);
  const steps = [
    {
      label: 'Choose a plan',
      detail: billingActive ? `${formatPlan(billing?.plan ?? 'Plan')} is active.` : 'Pick Solo or Team to unlock setup.',
      done: billingActive,
      icon: CreditCard,
      to: '/billing',
      action: billingActive ? 'Plan active' : 'Choose plan',
      completedTone: billingActive,
      statusOnly: billingActive,
    },
    ...(!engineCoreSetupComplete
      ? [
          {
            label: effectiveInstallerAcknowledged ? 'Install Oracord Engine' : engineDownload.label,
            detail: engineInstalled
              ? 'Engine has reported back as installed.'
              : effectiveInstallerAcknowledged
                ? 'Marked as installed. The page will verify it after Engine pairs and reports back.'
                : installerDownloaded
                  ? 'Downloaded. Open the installer on this Mac, finish the install, then click “I’ve installed it”.'
                : `${engineDownload.detail} ${engineDownload.platformNote}`,
            done: effectiveInstallerAcknowledged,
            icon: Download,
            to: engineDownload.path || '/settings',
            action: effectiveInstallerAcknowledged ? 'Installed' : installerDownloaded ? 'I’ve installed it' : engineDownload.path ? engineDownload.action : 'Open install help',
            externalDownload: engineDownload.externalDownload,
            buttonAction: installerDownloaded && !effectiveInstallerAcknowledged ? acknowledgeInstaller : undefined,
            onClick: engineDownload.externalDownload ? recordInstallerDownloaded : undefined,
            completedTone: effectiveInstallerAcknowledged,
            statusOnly: effectiveInstallerAcknowledged,
          },
        ]
      : []),
    ...(!engineCoreSetupComplete
      ? [
          {
            label: 'Pair the Engine',
            detail: !effectiveInstallerAcknowledged
              ? 'Install the Engine app you downloaded first, then pair it with your Oracord profile.'
              : 'Click once to create the secure pairing handoff and open Oracord Engine on this Mac.',
            done: engineHandoffReady,
            icon: Workflow,
            action: engineHandoffReady
              ? 'Paired'
              : !billingActive
                ? 'Choose plan first'
                : !effectiveInstallerAcknowledged
                  ? 'Install Engine first'
                  : 'Pair Engine',
            sourceStatus: engineHandoffStatus,
            to: pairingLaunchLink,
            externalAction: true,
            onClick: openPairingSetupLink,
            disabled: !billingActive || !activeOrganizationId || !effectiveInstallerAcknowledged || engineHandoffReady,
          },
        ]
      : []),
    {
      label: 'Confirm Engine is running',
      detail: engineRunning
        ? 'Oracord Engine is connected and currently reporting as running.'
        : !engineHandoffReady
          ? 'Pair the installed Engine before starting the background service on this Mac.'
          : 'Engine is installed and paired; start or restart the background service on this Mac.',
      done: engineRunning,
      icon: Workflow,
      to: engineRunning || !engineHandoffReady ? '/settings?tab=oracord' : engineStartLink,
      action: engineRunning ? 'Running' : !engineHandoffReady ? 'Pair first' : 'Start Engine',
      disabled: !engineRunning && !engineHandoffReady,
      externalAction: !engineRunning && engineHandoffReady,
      onClick: !engineRunning && engineHandoffReady ? openEngineStartLink : undefined,
      completedTone: engineRunning,
      statusOnly: engineRunning,
    },
    {
      label: soloMembership ? 'Connect Gmail & Calendar' : 'Add team members',
      detail: soloMembership
        ? soloGoogleConnected
          ? 'Your Google access is connected for this Solo workspace.'
          : googleWorkspaceConfigured
            ? 'Solo can use your personal Gmail or a Workspace account. Confirm Gmail and Calendar access next.'
            : 'Google connection is not configured yet; this is the next setup issue to resolve.'
        : activeProfileCount > 0
        ? `${activeProfileCount} Team profile${activeProfileCount === 1 ? '' : 's'} added. Open Team to invite work emails, connect Workspace, and finish numbers per person.`
        : 'Add the owner or first teammate with their company work email. Workspace and phone-number setup happen from Team.',
      done: soloMembership ? soloGoogleConnected : activeProfileCount > 0,
      icon: Mail,
      to: soloMembership ? '/setup' : '/members',
      action: soloMembership
        ? soloGoogleConnected
          ? 'Connected'
          : googleAuthorizationStarting
            ? 'Starting Google…'
            : 'Authorize Gmail and Calendar'
        : activeProfileCount > 0 ? 'Open Team' : 'Add team member',
      buttonAction: soloMembership && !soloGoogleConnected ? startGoogleAuthorization : undefined,
      disabled: soloMembership ? !googleWorkspaceConfigured || soloGoogleConnected || googleAuthorizationStarting : false,
      muted: soloMembership ? soloGoogleConnected : false,
      completedTone: soloMembership && soloGoogleConnected,
      statusOnly: soloMembership && soloGoogleConnected,
    },
    {
      label: 'Connect a business line',
      detail: businessLineConnected
        ? `${businessLineCount} business line${businessLineCount === 1 ? '' : 's'} connected for this workspace.`
        : soloMembership
          ? 'Add the phone number Oracord should use for this Solo workspace.'
          : 'Add or assign the first company phone number from Business Lines.',
      done: businessLineConnected,
      icon: Phone,
      to: '/numbers',
      action: businessLineConnected ? 'Connected' : 'Connect line',
      disabled: !billingActive,
      completedTone: businessLineConnected,
      statusOnly: businessLineConnected,
    },
  ];
  const setupGuide = setupGuideContent({
    billingActive,
    engineInstalled,
    installerAcknowledged: effectiveInstallerAcknowledged,
    engineHandoffReady,
    businessSetupLoaded,
    engineRunning,
    engineStartLink,
    activeProfileCount,
    soloMembership,
    soloGoogleConnected,
    googleWorkspaceConfigured,
    googleAuthorizationStarting,
    startGoogleAuthorization,
    businessLineConnected,
  });

  const launchDialogCopy = engineLaunchDialog?.kind === 'pair'
    ? {
        eyebrow: 'Engine setup',
        title: 'Open Oracord Engine?',
        description: 'Oracord will hand this company’s setup package to the Engine app installed on this Mac. Your browser may ask for permission one more time before Engine opens.',
        confirmLabel: 'Open Oracord Engine',
        details: [
          'This pairs the local Engine with the current company account.',
          'No setup code needs to be copied or pasted.',
          'After Engine reports back, this checklist will update automatically.',
        ],
      }
    : {
        eyebrow: 'Engine service',
        title: 'Start Oracord Engine?',
        description: 'Oracord will ask the local Engine app to start its background service so it can keep reporting setup status for this company.',
        confirmLabel: 'Start Engine',
        details: [
          'Engine runs locally on this Mac.',
          'The background service keeps Oracord connected after setup.',
          'If macOS asks for approval, choose Allow or Open.',
        ],
      };

  return (
    <>
      <AdminActionDialog
        open={Boolean(engineLaunchDialog)}
        eyebrow={launchDialogCopy.eyebrow}
        title={launchDialogCopy.title}
        description={launchDialogCopy.description}
        confirmLabel={launchDialogCopy.confirmLabel}
        cancelLabel="Stay here"
        details={launchDialogCopy.details}
        footerNote="Tip: if your browser shows a second system prompt, approve it so Oracord can continue setup."
        onCancel={() => setEngineLaunchDialog(null)}
        onConfirm={confirmEngineLaunch}
      />
      <PageHeader
        eyebrow="Setup"
        title="Set up Oracord"
        description="Use this page for the basics: plan, Engine, Google, then business line."
      />
      <div className="grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
        <SectionCard className="p-5 sm:p-6">
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">First run</div>
            <h2 className="mt-2 text-lg font-bold text-slate-950">Your setup checklist</h2>
            <p className="mt-1 text-sm text-slate-600">Complete the basics here: plan, Engine, Google, then the business line.</p>
          </div>
          <div className="mb-5 grid gap-2 rounded-2xl border border-slate-100 bg-slate-50/70 p-3 sm:grid-cols-3">
            {engineStatusItems.map(item => (
              <div key={item.mark} className="rounded-xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{item.label}</div>
                <div className={`mt-1 text-sm font-bold ${item.done ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {item.done ? item.readyLabel : item.notReadyLabel}
                </div>
              </div>
            ))}
          </div>
          {showEngineInstallCard && (
            <div className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">Step 1</div>
                  <h3 className="mt-1 text-sm font-bold text-emerald-950">Download Oracord Engine first</h3>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-800">
                    {engineDownload.detail} {engineDownload.platformNote}
                  </p>
                </div>
                {engineDownload.path ? (
                  <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                    <a
                      href={engineDownload.path}
                      download
                      onClick={recordInstallerDownloaded}
                      className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-950 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                    >
                      {engineDownload.action}
                    </a>
                    <button
                      type="button"
                      onClick={acknowledgeInstaller}
                      className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
                    >
                      I’ve installed it
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/settings"
                    className="inline-flex shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-950 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    Open install help
                  </Link>
                )}
              </div>
            </div>
          )}
          {showGoogleAccountMismatchNote && (
            <div className={`mb-4 rounded-2xl border p-4 text-sm leading-6 ${signedInWithPersonalGoogle ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-blue-100 bg-blue-50 text-blue-900'}`}>
              <p className="font-semibold">Signed in as {signedInEmail}</p>
              <p className="mt-1">
                {signedInWithPersonalGoogle
                  ? 'This is a personal Google account. You can keep it as the admin login, but Oracord cannot connect company Gmail or Calendar from it. Add your company Workspace email as a Team member, then connect Workspace from that Team profile.'
                  : 'To connect Workspace, add the company work email as a Team member and use that Team profile for Google authorization.'}
              </p>
            </div>
          )}
          {googleAuthorizationError && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              {googleAuthorizationError}
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {steps.map(step => {
              const Icon = step.icon;
              const StatusIcon = step.done ? CheckCircle2 : Circle;
              const actionClass = 'completedTone' in step && step.completedTone
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-300 hover:bg-emerald-100'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50';
              return (
                <div key={step.label} className={`flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between ${'muted' in step && step.muted ? 'opacity-60' : ''}`}>
                  <div className="flex gap-3">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-slate-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusIcon className={`h-4 w-4 ${step.done ? 'text-emerald-600' : 'text-slate-300'}`} />
                        <h3 className="text-sm font-bold text-slate-950">{step.label}</h3>
                        {step.sourceStatus && <SourceStatusPill status={step.sourceStatus} />}
                      </div>
                      <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">{step.detail}</p>
                      {'sourceStatus' in step && openedSetupLink && step.sourceStatus && <p className="mt-1 text-xs leading-5 text-blue-800">{step.sourceStatus.detail}</p>}
                    </div>
                  </div>
                  {'statusOnly' in step && step.statusOnly ? (
                    <span className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm ${actionClass}`}>
                      {step.action}
                    </span>
                  ) : 'buttonAction' in step && step.buttonAction ? (
                    <button
                      type="button"
                      disabled={'disabled' in step && step.disabled}
                      onClick={step.buttonAction}
                      className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${actionClass}`}
                    >
                      {step.action}
                    </button>
                  ) : 'disabled' in step && step.disabled ? (
                    <span className="inline-flex shrink-0 cursor-not-allowed items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-500 shadow-sm">
                      {step.action}
                    </span>
                  ) : step.externalDownload || step.externalAction ? (
                    <a
                      href={step.to}
                      download={step.externalDownload || undefined}
                      onClick={step.onClick}
                      className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${actionClass}`}
                    >
                      {step.action}
                    </a>
                  ) : (
                    <Link
                      to={step.to}
                      className={`inline-flex shrink-0 items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition ${actionClass}`}
                    >
                      {step.action}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
        <SectionCard className="p-5 sm:p-6">
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">What’s happening now</div>
            <h2 className="mt-2 text-lg font-bold text-slate-950">{setupGuide.title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">{setupGuide.description}</p>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4 text-sm leading-6 text-slate-600">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Why this matters</div>
            <p className="mt-2">{setupGuide.why}</p>
          </div>
          <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-blue-900">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-500">Next action</div>
            <p className="mt-2">{setupGuide.next}</p>
          </div>
          {showGoogleAccountMismatchNote && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              If this admin login is personal Gmail, add the company Workspace email as a Team member before connecting Gmail and Calendar.
            </div>
          )}
          {'buttonAction' in setupGuide && setupGuide.buttonAction ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={setupGuide.buttonAction}
                disabled={setupGuide.disabled}
                className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {setupGuide.action}
              </button>
            </div>
          ) : setupGuide.to && (
            <div className="mt-4 flex flex-wrap gap-2">
              {'externalAction' in setupGuide && setupGuide.externalAction ? (
                <a
                  href={setupGuide.to}
                  onClick={openEngineStartLink}
                  className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                >
                  {setupGuide.action}
                </a>
              ) : (
                <Link to={setupGuide.to} className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50">
                  {setupGuide.action}
                </Link>
              )}
            </div>
          )}
        </SectionCard>
      </div>
    </>
  );
}


function emailDomain(email: string | null): string | null {
  const at = email?.lastIndexOf('@') ?? -1;
  return at >= 0 ? email!.slice(at + 1).trim().toLowerCase() : null;
}

function SourceStatusPill({ status }: { status: SetupSourceStatus }) {
  const classes = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-800 ring-amber-100',
    blue: 'bg-blue-50 text-blue-800 ring-blue-100',
    slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  } satisfies Record<SetupSourceStatus['tone'], string>;

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] ring-1 ${classes[status.tone]}`}>
      {status.label}
    </span>
  );
}

function buildOracordEnginePairingLaunchLink(organizationId: string) {
  const params = new URLSearchParams({ organizationId });
  return `${API_BASE_URL}/api/admin/settings/oracord-engine/pairing-launch?${params.toString()}`;
}

function formatPlan(plan: string) {
  return plan
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');
}

function isSoloPlan(plan: string | null) {
  return Boolean(plan && /(^|[-_\s])solo($|[-_\s])/.test(plan.toLowerCase()));
}

function installerStorageKey(scope: string, key: 'downloaded' | 'installed-acknowledged') {
  return `oracord-engine:${scope}:${key}`;
}

function setupGuideContent({
  billingActive,
  engineInstalled,
  installerAcknowledged,
  engineHandoffReady,
  businessSetupLoaded,
  engineRunning,
  engineStartLink,
  activeProfileCount,
  soloMembership,
  soloGoogleConnected,
  googleWorkspaceConfigured,
  googleAuthorizationStarting,
  startGoogleAuthorization,
  businessLineConnected,
}: {
  billingActive: boolean;
  engineInstalled: boolean;
  installerAcknowledged: boolean;
  engineHandoffReady: boolean;
  businessSetupLoaded: boolean;
  engineRunning: boolean;
  engineStartLink: string;
  activeProfileCount: number;
  soloMembership: boolean;
  soloGoogleConnected: boolean;
  googleWorkspaceConfigured: boolean;
  googleAuthorizationStarting: boolean;
  startGoogleAuthorization: () => void;
  businessLineConnected: boolean;
}) {
  if (!engineInstalled && !installerAcknowledged) {
    return {
      title: 'Install the local Engine',
      description: 'The Engine is the small Mac helper that lets Oracord do local work for this company instead of treating setup like a cloud-only dashboard.',
      why: 'Installing it creates the local runtime that can pair with this company, receive its setup package, and later process the work that belongs on this Mac.',
      next: 'Download the installer, open it, approve the Mac prompts, then click “I’ve installed it” so Setup can move you to pairing.',
      to: null,
      action: null,
    };
  }
  if (!engineInstalled && installerAcknowledged) {
    return {
      title: 'Pair the installed Engine',
      description: 'The web app cannot reliably detect a Mac install until Engine opens a setup link and reports back.',
      why: 'Pairing is the visible handoff: it proves the local app can receive this company’s setup package and update the checklist.',
      next: 'Click Pair Engine once. Oracord creates the secure handoff and opens the local Engine prompt from that same click. Approve the prompt so Engine can report back.',
      to: null,
      action: null,
    };
  }
  if (!billingActive) {
    return {
      title: 'Choose the starting plan',
      description: 'Billing unlocks the company setup path. Solo is for one user and one business line; Team includes three users and three business lines.',
      why: 'The plan tells Oracord how many business lines and user accounts are included before you start assigning staff.',
      next: 'Pick Solo or Team. Extra business lines can be added later if the team grows beyond the included lines.',
      to: '/billing',
      action: 'Choose plan',
    };
  }
  if (!engineHandoffReady) {
    return {
      title: 'Pair this company to Engine',
      description: 'Pairing connects the installed Mac helper to this company account without making you copy tokens or codes manually.',
      why: 'The secure setup link tells the local Engine which company it belongs to and where to report status.',
      next: 'Click Pair Engine once. Oracord creates the secure handoff and opens the local Engine prompt from that same click. Approve the prompt so Engine can report back.',
      to: null,
      action: null,
    };
  }
  if (!engineRunning) {
    return {
      title: 'Start the background service',
      description: 'The Engine is installed and paired. The last local step is making sure its background service is running.',
      why: businessSetupLoaded
        ? 'The service keeps Engine available after setup, restarts, or sleep so Oracord can keep checking in without a dashboard tab open.'
        : 'The service keeps Engine available after setup, restarts, or sleep. Company setup details load locally in the background and do not need a separate admin checklist item.',
      next: 'Click Start Engine here. If macOS asks for approval, allow it so the service can run in the background.',
      to: engineStartLink,
      action: 'Start Engine',
      externalAction: true,
    };
  }
  if (soloMembership) {
    if (soloGoogleConnected) {
      if (!businessLineConnected) {
        return {
          title: 'Connect the business line',
          description: 'Google is connected. The next setup item is the phone number Oracord should use for this Solo workspace.',
          why: 'A business line is the source Oracord uses to attach calls, recordings, transcripts, and history to the right workspace.',
          next: 'Open Business Lines and add or assign the first phone number for this workspace.',
          to: '/numbers',
          action: 'Connect line',
        };
      }
      return {
        title: 'Core setup is connected',
        description: 'Solo setup now has Engine, Gmail and Calendar, and a business line connected.',
        why: 'Those are the source connections Oracord needs before live calls and work history can start flowing into the product.',
        next: 'You can leave Setup here or open Business Lines if you want to review the connected phone number.',
        to: '/numbers',
        action: 'View business line',
      };
    }
    return {
      title: 'Confirm Gmail and Calendar access',
      description: googleWorkspaceConfigured
        ? 'Solo does not require Google Workspace. A personal Gmail account can authorize Gmail and Calendar access for the owner.'
        : 'Google authorization is not configured yet, so this is the current setup issue to resolve before Solo can connect Gmail and Calendar.',
      why: 'The next useful check is the Google consent path, not adding teammates. Solo is one owner by design.',
      next: googleWorkspaceConfigured
        ? 'Authorize Gmail and Calendar directly from Setup with the Google account you want Oracord to use.'
        : 'Add the Google OAuth configuration, then return here and confirm the owner connection.',
      to: null,
      action: googleWorkspaceConfigured
        ? googleAuthorizationStarting
          ? 'Starting Google…'
          : 'Authorize Gmail and Calendar'
        : null,
      buttonAction: googleWorkspaceConfigured ? startGoogleAuthorization : undefined,
      disabled: googleAuthorizationStarting,
    };
  }
  if (activeProfileCount === 0) {
    return {
      title: 'Add the first team profile',
      description: 'Now that the local setup is ready, add the owner or first teammate with their company work email.',
      why: 'Team profiles are where Workspace connection and business-line assignment happen for each person.',
      next: 'Add the first profile from Team. If the admin login is personal Gmail, use the company Workspace email for the Team profile.',
      to: '/members',
      action: 'Open Team',
    };
  }
  return {
    title: 'Setup is ready for the next teammate',
    description: 'The core setup is in place. Add more staff only when you are ready to assign Workspace context or business lines.',
    why: 'Keeping Team setup separate avoids clutter during the first install while still giving you a clear path when staffing is the next real job.',
    next: 'Continue in Team when you need to add another staff member or assign another business line.',
    to: '/members',
    action: 'Open Team',
  };
}
